import { createAsyncThunk } from "@reduxjs/toolkit";
import { showSnackbar } from "../slices/SnackbarSlice";
import {
  addPhotoGalleryWidgetApi,
  editPhotoGalleryWidgetApi,
  getPhotoGalleryWidgetConfigApi,
  getPhotoGalleryWidgetListApi,
  getPhotoGalleryWidgetJsonldApi,
  addPostImageApi,
  IGetPhotoGalleryWidgetConfigPayload,
  IGetPhotoGalleryWidgetListPayload,
  IGetPhotoGalleryWidgetJsonldPayload,
  IAddPostImagePayload,
} from "../apis/PhotoGalleryWidgetApi";

/**
 * Create a new photo gallery widget.
 * Payload: { checkin_id, widget_name, ...optionalPhotoGalleryKindFields }
 */
export const addPhotoGalleryWidgetAction = createAsyncThunk(
  "photoGalleryWidget/add",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await addPhotoGalleryWidgetApi(payload);
      dispatch(
        showSnackbar({
          message:
            response?.data?.message ||
            "Photo gallery widget created successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to create photo gallery widget",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to create photo gallery widget",
      );
    }
  },
);

/**
 * Edit photo-gallery-kind-specific fields on an existing widget.
 * Payload: { widget_id, ...partialPhotoGalleryKindFields }
 * For common fields (name, border_config, theme_config, cta_config, branding_config)
 * use editWidgetAction (shared endpoint) from WidgetActions.ts.
 */
export const editPhotoGalleryWidgetAction = createAsyncThunk(
  "photoGalleryWidget/edit",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await editPhotoGalleryWidgetApi(payload);
      dispatch(
        showSnackbar({
          message:
            response?.data?.message ||
            "Photo gallery widget updated successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            "Failed to update photo gallery widget",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to update photo gallery widget",
      );
    }
  },
);

/**
 * Fetch the bootstrap config for the dashboard editor / preview.
 * Returns: { widget: <full normalized widget doc>, business_name, business_logo, logo_placement }.
 */
export const getPhotoGalleryWidgetConfigAction = createAsyncThunk(
  "photoGalleryWidget/getConfig",
  async (
    payload: IGetPhotoGalleryWidgetConfigPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await getPhotoGalleryWidgetConfigApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to fetch photo gallery widget config",
      );
    }
  },
);

/**
 * Fetch the paginated list of project posts / pairs for a widget.
 * Payload: { business_id, widget_id?, cursor?, limit? }
 */
export const getPhotoGalleryWidgetListAction = createAsyncThunk(
  "photoGalleryWidget/getList",
  async (
    payload: IGetPhotoGalleryWidgetListPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await getPhotoGalleryWidgetListApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to fetch photo gallery widget list",
      );
    }
  },
);

/**
 * Fetch JSON-LD structured data for SEO.
 */
export const getPhotoGalleryWidgetJsonldAction = createAsyncThunk(
  "photoGalleryWidget/getJsonld",
  async (
    payload: IGetPhotoGalleryWidgetJsonldPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await getPhotoGalleryWidgetJsonldApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to fetch photo gallery widget JSON-LD",
      );
    }
  },
);

/**
 * Add an image URL to an existing post.
 */
export const addPostImageAction = createAsyncThunk(
  "photoGalleryWidget/addPostImage",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await addPostImageApi(payload);
      dispatch(
        showSnackbar({
          message:
            response?.data?.message || "Image added to post successfully!",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message || "Failed to add image to post",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to add image to post",
      );
    }
  },
);
